import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CancelIcon from '@mui/icons-material/Cancel';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import Skeleton from '@mui/material/Skeleton';
import type { SxProps, Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { ComponentType, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AcceptedIcon } from '@/assets/icons/AcceptedIcon';
import { PartiallyAcceptedIcon } from '@/assets/icons/PartiallyAcceptedIcon';
import { PendingReviewIcon } from '@/assets/icons/PendingReviewIcon';
import { RejectedIcon } from '@/assets/icons/RejectedIcon';
import { BasicChip } from '@/components/Chips/BasicChip';
import {
	statusChipColors,
	StatusChip,
	StatusKind
} from '@/components/Chips/StatusChip/StatusChip';
import { DcrDisplayStatus } from '@/core/graphql/generated/types';
import { formatThousandSeparator } from '@/utils/formatters/formatThousandSeparator';

import { statusFilterStyles as sx } from './StatusFilter.styles';

const LABEL_ID = 'dcr-status-filter-label';

export type StatusFilterValue = Exclude<
	DcrDisplayStatus,
	DcrDisplayStatus.ChangeProcessing
>;

const STATUS_ORDER: StatusFilterValue[] = [
	DcrDisplayStatus.ChangePendingreview,
	DcrDisplayStatus.ChangeRejected,
	DcrDisplayStatus.ChangePartial,
	DcrDisplayStatus.ChangeAccepted
];

const STATUS_KIND_BY_VALUE: Record<
	StatusFilterValue,
	Exclude<StatusKind, 'processing'>
> = {
	[DcrDisplayStatus.ChangePendingreview]: 'pending',
	[DcrDisplayStatus.ChangePartial]: 'partial',
	[DcrDisplayStatus.ChangeAccepted]: 'accepted',
	[DcrDisplayStatus.ChangeRejected]: 'rejected'
};

const chipColorSx = (value: StatusFilterValue) => {
	const { backgroundColor, color } =
		statusChipColors[STATUS_KIND_BY_VALUE[value]];
	return { backgroundColor, color };
};

const STATUS_ICON_BY_VALUE: Record<
	StatusFilterValue,
	ComponentType<{ sx?: SxProps<Theme>; fill?: string }>
> = {
	[DcrDisplayStatus.ChangePendingreview]: PendingReviewIcon,
	[DcrDisplayStatus.ChangePartial]: PartiallyAcceptedIcon,
	[DcrDisplayStatus.ChangeAccepted]: AcceptedIcon,
	[DcrDisplayStatus.ChangeRejected]: RejectedIcon
};

export type StatusFacet = { status: DcrDisplayStatus; count: number };

type Props = {
	selectedValues: StatusFilterValue[];
	handleChange: (values: StatusFilterValue[]) => void;
	variant?: 'default' | 'chip';
	facetCounts?: StatusFacet[];
	loading?: boolean;
};

export const StatusFilter = ({
	selectedValues,
	handleChange,
	variant = 'default',
	facetCounts,
	loading = false
}: Props) => {
	const { t } = useTranslation();

	const labelByStatus = useMemo(
		(): Record<StatusFilterValue, string> => ({
			[DcrDisplayStatus.ChangePendingreview]: t('Pending Review'),
			[DcrDisplayStatus.ChangePartial]: t('Partially Accepted'),
			[DcrDisplayStatus.ChangeAccepted]: t('Accepted'),
			[DcrDisplayStatus.ChangeRejected]: t('Rejected')
		}),
		[t]
	);

	const statuses = useMemo(
		() =>
			STATUS_ORDER.map(value => ({
				id: value,
				name: labelByStatus[value]
			})),
		[labelByStatus]
	);

	const nameById = useMemo(
		() => new Map(statuses.map(status => [status.id, status.name])),
		[statuses]
	);

	const countById = useMemo(
		() => new Map(facetCounts?.map(facet => [facet.status, facet.count])),
		[facetCounts]
	);

	const selectableOptions = useMemo(
		() =>
			statuses
				.map(status => ({ ...status, count: countById.get(status.id) }))
				.filter(option => !selectedValues.includes(option.id)),
		[statuses, countById, selectedValues]
	);
	const hasSelectableOptions = selectableOptions.length > 0;

	const [open, setOpen] = useState(false);

	const handleOpen = () => {
		if (hasSelectableOptions) setOpen(true);
	};

	const blurActiveElement = () => {
		window.setTimeout(() => {
			(document.activeElement as HTMLElement | null)?.blur();
		}, 0);
	};

	const handleClose = () => {
		setOpen(false);
		blurActiveElement();
	};

	const handleSelectChange = (
		event: SelectChangeEvent<StatusFilterValue[]>
	) => {
		const { value } = event.target;
		const nextValues = (
			typeof value === 'string' ? value.split(',') : value
		) as StatusFilterValue[];

		handleChange(nextValues);
		handleClose();
	};

	const handleDeselect = (value: StatusFilterValue) => {
		handleChange(selectedValues.filter(selected => selected !== value));
		blurActiveElement();
	};

	if (loading) {
		return (
			<Box sx={sx.container}>
				<Skeleton variant='rounded' sx={sx.filterSkeleton} />
			</Box>
		);
	}

	return (
		<Box sx={sx.container}>
			<FormControl size='small' sx={sx.root}>
				<Select
					labelId={LABEL_ID}
					multiple
					displayEmpty
					sx={sx.select}
					open={open && hasSelectableOptions}
					onOpen={handleOpen}
					onClose={handleClose}
					value={selectedValues}
					onChange={handleSelectChange}
					IconComponent={hasSelectableOptions ? ArrowDropDownIcon : () => null}
					MenuProps={{
						disableAutoFocusItem: true,
						slotProps: { list: { sx: sx.menuList } }
					}}
					renderValue={selected => {
						if (selected.length === 0) {
							return (
								<Box component='span' sx={sx.placeholder}>
									{t('Filter by Status')}
								</Box>
							);
						}

						return variant === 'chip' ? (
							<Box sx={sx.chipContainer}>
								{selected.map(value => {
									const Icon = STATUS_ICON_BY_VALUE[value];
									return (
										<BasicChip
											key={value}
											label={
												countById.get(value) !== undefined
													? `${nameById.get(value) ?? value} (${formatThousandSeparator(countById.get(value)!)})`
													: (nameById.get(value) ?? value)
											}
											variant='basic'
											customSx={chipColorSx(value)}
											icon={<Icon sx={sx.chipIcon} fill='currentColor' />}
											onDelete={() => handleDeselect(value)}
											deleteIcon={
												<CancelIcon
													fontSize='small'
													onMouseDown={event => {
														event.preventDefault();
														event.stopPropagation();
													}}
												/>
											}
										/>
									);
								})}
							</Box>
						) : (
							<Box component='span' sx={sx.textValue}>
								{selected.map(value => nameById.get(value) ?? value).join(', ')}
							</Box>
						);
					}}
				>
					{selectableOptions.map(option => (
						<MenuItem key={option.id} value={option.id} sx={sx.menuItem}>
							<StatusChip status={STATUS_KIND_BY_VALUE[option.id]} />
							{option.count !== undefined && (
								<Typography component='span' sx={sx.menuItemCount}>
									{`(${formatThousandSeparator(option.count)})`}
								</Typography>
							)}
						</MenuItem>
					))}
				</Select>
			</FormControl>
		</Box>
	);
};
